import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { organizationTypeService } from "../services/organization-type.service";
import type {
	CreateOrganizationTypeRequest,
	ErrorResponse,
	PaginationParams,
	UpdateOrganizationTypeRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const orgTypeKeys = {
	all: ["organization-types"] as const,
	lists: () => [...orgTypeKeys.all, "list"] as const,
	list: (params?: PaginationParams) => [...orgTypeKeys.lists(), params] as const,
	details: () => [...orgTypeKeys.all, "detail"] as const,
	detail: (id: string) => [...orgTypeKeys.details(), id] as const,
};

// ─── useOrganizationTypes (list) ────────────────────────────────────
export function useOrganizationTypes(params?: PaginationParams) {
	return useQuery({
		queryKey: orgTypeKeys.list(params),
		queryFn: () => organizationTypeService.list(params),
	});
}

// ─── useOrganizationType (getById) ──────────────────────────────────
export function useOrganizationType(id: string) {
	return useQuery({
		queryKey: orgTypeKeys.detail(id),
		queryFn: () => organizationTypeService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateOrganizationType ──────────────────────────────────────
export function useCreateOrganizationType() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateOrganizationTypeRequest) => organizationTypeService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgTypeKeys.lists() });
			message.success("Tashkilot turi muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Tashkilot turi yaratishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateOrganizationType ──────────────────────────────────────
export function useUpdateOrganizationType() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationTypeRequest }) =>
			organizationTypeService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: orgTypeKeys.lists() });
			queryClient.invalidateQueries({
				queryKey: orgTypeKeys.detail(variables.id),
			});
			message.success("Tashkilot turi muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Tashkilot turi yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useDeleteOrganizationType ──────────────────────────────────────
export function useDeleteOrganizationType() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => organizationTypeService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: orgTypeKeys.lists() });
			message.success("Tashkilot turi muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Tashkilot turi o'chirishda xatolik";
			message.error(msg);
		},
	});
}
